import { defineStore } from "pinia"
import Cookies from "js-cookie"
import axios from "@/plugins/axios"
import type { AuthTypes } from "@/types"
import {
	evaluateRule,
	type Permission,
	type PermissionRule,
} from "@/shared/permissions"

type LoginCommit = {
	token: string
	refreshToken: string
	user: AuthTypes.User
}

export type StripeGate = {
	applicable: boolean        // false → caller doesn't need Stripe (advertiser, agency)
	ready:      boolean        // true  → write-side endpoints will accept the caller
	status:     string | null  // 'pending' | 'enabled' | 'disabled' | null
	payouts_enabled:   boolean
	details_submitted: boolean
}

export const useAuthStore = defineStore("auth", {
	state: (): AuthTypes.AuthInterface & { stripe: StripeGate } => ({
		token: null,
		refreshToken: null,
		user: null,
		permissions: [],
		stripe: {
			applicable: false,
			ready: false,
			status: null,
			payouts_enabled: false,
			details_submitted: false,
		},
	}),

	getters: {
		isAuthenticated: (state): boolean => !!state.token,
		permissionSet: (state): ReadonlySet<Permission> => new Set(state.permissions),
		/** True when caller is a venue owner whose Stripe onboarding still
		 *  has to be completed. Cabinet uses this to hide write-side CTAs
		 *  (Add screen, Add location, Bind, …). */
		needsStripeOnboarding: (state): boolean =>
			state.stripe.applicable && !state.stripe.ready,
	},

	actions: {
		commitLogin(payload: LoginCommit) {
			this.token = payload.token
			this.refreshToken = payload.refreshToken
			this.user = payload.user
		},

		commitRefresh(token: string, refreshToken: string) {
			this.token = token
			this.refreshToken = refreshToken
		},

		async syncFromServer(): Promise<void> {
			if (!this.isAuthenticated) return
			try {
				const r = await axios.get("api/v1/auth/me")
				const attrs = r.data?.data?.attributes ?? {}
				const id    = r.data?.data?.id
				const perms = r.data?.meta?.permissions ?? []
				const stripe = r.data?.meta?.stripe
				// Sync the UI language with what's stored on the user row.
				// Lazy import to dodge the circular dep (authStore → axios →
				// localeStore → … if we did a top-level import).
				if (attrs?.preferred_locale) {
					const { useLocaleStore } = await import("@/store/localeStore")
					useLocaleStore().adoptFromServer(attrs.preferred_locale)
				}
				this.user = { id, ...attrs }
				this.permissions = perms
				if (stripe) {
					this.stripe = {
						applicable:        !!stripe.applicable,
						ready:             !!stripe.ready,
						status:            stripe.status ?? null,
						payouts_enabled:   !!stripe.payouts_enabled,
						details_submitted: !!stripe.details_submitted,
					}
				}
			} catch { /* offline / 401 — leave local state alone, interceptor handles 401 */ }
		},

		setUser(user: AuthTypes.User) {
			this.user = user
		},

		async logout() {
			try {
				await axios.post("api/v1/auth/logout", { refresh_token: this.refreshToken })
			} catch { /* swallow — local wipe still happens */ }
			this.$reset()
		},

		can(rule: PermissionRule): boolean {
			if (!this.isAuthenticated) return false
			return evaluateRule(this.permissionSet, rule)
		},
	},

	persist: {
		key: "auth",
		storage: {
			getItem: (key: string) => Cookies.get(key) as string,
			setItem: (key: string, value: string) =>
				Cookies.set(key, value, { expires: 365 }),
		},
	},
})
